import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import type {
  MergeErrorCode,
  MergeSubmitOutcome,
  MergeSubmitRequest,
  MergeSubmitResponse,
} from '../api/mergeResolution.types';
import { MergeApiError, toMergeApiError } from '../api/MergeApiError';
import type {
  MergeResolutionDataSource,
  MergeRouteIds,
} from '../api/MergeResolutionDataSource';
import { SUBMIT_ROUTE_ENABLED } from '../config';
import { sendableDecisions } from '../state/rebaseApply';
import { detectStructuralBlock } from '../state/structural';
import type { CellKey, RebaseApplied } from '../state/types';

import type { MergeSessionResult } from './useMergeSession';

export interface SubmitFlowResult {
  submit: () => Promise<void>;
  submitting: boolean;
  // `MergeSubmitOutcome` is `MergeSubmitResponse['Outcome']`; the existing
  // alias is reused rather than re-declared here.
  outcome?: MergeSubmitOutcome;
  // Count of cells the submit-time rebase reopened, or null when there is
  // nothing to show. The shared rebase also sets `useMergeSession`'s own
  // `newConflicts`, but that one follows every rebase; this one is set only
  // where a submit stopped because cells had reopened.
  newConflicts: number | null;
  dismissNewConflicts: () => void;
}

// One rebase, then at most one more pass after a REBASE_REQUIRED that came
// back from submit itself. Never a loop.
const MAX_ATTEMPTS = 2;

// Chosen here; the plan fixes no strings for these. None of them carries a
// cell value, a row identifier or a submission id.
const QUEUED_MESSAGE = 'Resolution submitted. Recalculation queued.';
const PROMOTED_MESSAGE = 'Resolution submitted and promoted.';
const SERVER_FAILED_MESSAGE = 'The server reported that the submission failed.';
const REBASE_EXHAUSTED_MESSAGE =
  'The current version moved again while submitting. Review the conflicts and try again.';

// Another user won the claim on this version (409 RESOLUTION_UNAVAILABLE).
// The text follows `State`; an IN_PROGRESS winner is never named.
const RESOLUTION_IN_PROGRESS_MESSAGE =
  'Another user is resolving this version right now. Please wait a moment and refresh to see the result.';
const RESOLUTION_GENERIC_MESSAGE =
  'This version has been or is being resolved by another user. Refresh to see the latest.';
// The fallback is cased by where the name lands: mid-sentence in the RESOLVED
// text, sentence-initial in the FAILED text.
const resolverName = (
  name?: string | null,
  fallback = 'Another user'
): string => (name?.trim() ? name.trim() : fallback);
const resolvedMessage = (name?: string | null): string =>
  `Looks like ${resolverName(
    name,
    'another user'
  )} got to this one first. Their resolution has been applied, so yours wasn't needed. Refresh to see the updated version.`;
const failedMessage = (name?: string | null): string =>
  `${resolverName(
    name
  )} tried to resolve this version, but it didn't complete. Refresh to see the current state and save your changes again if needed.`;

export const resolutionUnavailableMessage = (err: MergeApiError): string => {
  if (err.state === 'IN_PROGRESS') {
    return RESOLUTION_IN_PROGRESS_MESSAGE;
  }
  if (err.state === 'RESOLVED') {
    return resolvedMessage(err.resolvedByName);
  }
  if (err.state === 'FAILED') {
    return failedMessage(err.resolvedByName);
  }
  return RESOLUTION_GENERIC_MESSAGE;
};

// Submit's own refusals, by code; any other code shows the server's message.
// Draft wording, pending owner / UX confirmation. FORBIDDEN is submit's bare
// 403 (`can_create_submission` failed); SUBMISSION_NOT_FOUND is a save that is
// no longer pending (already submitted or failed) or not the caller's.
const SUBMIT_ERROR_MESSAGES: Partial<Record<MergeErrorCode, string>> = {
  GROUP_LOCKED: 'This group is locked, so changes to it cannot be submitted.',
  FORBIDDEN: 'You do not have permission to submit changes to this scenario.',
  SUBMISSION_NOT_FOUND:
    'This save is no longer waiting for resolution, so it cannot be submitted. It may already have been submitted or failed.',
  REBASE_REQUIRED: REBASE_EXHAUSTED_MESSAGE,
};

/**
 * The lost-update gate for the resolve-conflicts screen.
 *
 * `submit()` never sends decisions against a base that may have moved: it
 * rebases first, and it refuses to submit when that rebase shows the user
 * has something left to decide. Nothing is marked resolved or submitted on
 * any failure path.
 */
export function useSubmitFlow(
  ds: MergeResolutionDataSource,
  ids: MergeRouteIds,
  session: MergeSessionResult
): SubmitFlowResult {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<MergeSubmitOutcome | undefined>(
    undefined
  );
  const [newConflicts, setNewConflicts] = useState<number | null>(null);

  // Mirror of the session for the async body: it reads the newest decisions
  // and ids without `submit` depending on every session render.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The double-click guard has to be synchronous. `submitting` is state and
  // is not visible to a second call made in the same tick, so the footer
  // button being disabled on it is a second line of defence, not the first.
  const inFlightRef = useRef(false);

  const dismissNewConflicts = useCallback(() => {
    setNewConflicts(null);
  }, []);

  // Exactly what `useMergeSession`'s own rebase commits, so the screen lands
  // in the same post-rebase state whichever hook ran the rebase.
  // `rebase/applied` does not carry serverDecisionKeys, hence the second
  // dispatch. They are what the running counts (and so the Submit gate) are
  // measured from: decisions after the rebase are counted against them, so
  // leaving them out would count the rebased decisions twice.
  const commitRebase = useCallback((applied: RebaseApplied) => {
    const { dispatch } = sessionRef.current;
    dispatch({ type: 'rebase/applied', result: applied });
    dispatch({
      type: 'counts/loaded',
      counts: applied.serverCounts,
      serverDecisionKeys: Object.keys(applied.decisions) as CellKey[],
    });
  }, []);

  const buildSubmitRequest = useCallback(
    (applied: RebaseApplied): MergeSubmitRequest => {
      const state = sessionRef.current.state;
      const req: MergeSubmitRequest = {
        GroupRowIdentifierID: state.session.groupRowIdentifierId,
        MineSource: 'staging',
        SubmissionID: state.session.submissionId,
        // The ids and decisions the rebase just established, not the
        // pre-rebase ones. A decision naming a NoDecisionNeeded cell is
        // refused by the server (400 INVALID_DECISION), so it is never sent.
        CurrentSubmissionID: applied.session.currentSubmissionId,
        Decisions: sendableDecisions(
          applied.decisions,
          applied.noDecisionNeeded
        ),
      };
      // That rebase's `Reopened` echo, unchanged: the server checks every
      // carried cell in the diff has a decision. Omitted when empty.
      if (applied.carrier.length > 0) {
        req.Reopened = applied.carrier;
      }
      return req;
    },
    []
  );

  const announce = useCallback(
    (res: MergeSubmitResponse) => {
      setOutcome(res.Outcome);
      if (res.Outcome === 'FAILED') {
        // The call succeeded but the operation did not. Staying put keeps
        // the resolution on screen; navigating away would hide the failure.
        enqueueSnackbar(SERVER_FAILED_MESSAGE, { variant: 'error' });
        return;
      }
      enqueueSnackbar(
        res.Outcome === 'PROMOTED' ? PROMOTED_MESSAGE : QUEUED_MESSAGE,
        { variant: 'success' }
      );
      navigate(-1);
    },
    [enqueueSnackbar, navigate]
  );

  const submit = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) {
      return;
    }
    // Kill switch. POST …/merge/submit ships with CLD-2971 and the flag is
    // on; turning it off fails closed before any request is made, since a
    // rebase run for a submit that cannot happen would move the base for no
    // reason. The footer gate already states the reason, so this is
    // deliberately silent — never an error dialog.
    if (!SUBMIT_ROUTE_ENABLED) {
      return;
    }

    inFlightRef.current = true;
    // True for the whole sequence, rebase included.
    setSubmitting(true);
    setOutcome(undefined);

    try {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        // Step 1. Always before the submit, never after, and it is the
        // session's rebase rather than a second one of our own: it is
        // single-flight, so a background REBASE_REQUIRED and this submit
        // await one call instead of two rebases advancing the current
        // submission in parallel. Ids and decisions are no longer threaded
        // through this loop - the shared rebase commits its result before it
        // resolves, so a second pass already reads the rebase that answered.
        let applied: RebaseApplied;
        try {
          applied = await sessionRef.current.actions.rebase();
        } catch (err) {
          // A rebase 404 means the save is missing, not that another user
          // resolved it (a non-pending row answers 409 RESOLUTION_UNAVAILABLE),
          // so it shows the SUBMISSION_NOT_FOUND message, not RESOLVED.
          throw toMergeApiError(err);
        }
        if (!mountedRef.current) {
          return;
        }

        // Step 2 — the lost-update guard. A reopened cell means the current
        // version moved underneath a decision the user already made, so the
        // submit stops here. There is no bypass. A NoDecisionNeeded cell is
        // not in `reopened` (it asks no question), so it does not stop it.
        if (applied.reopened.length > 0) {
          commitRebase(applied);
          setNewConflicts(applied.reopened.length);
          return;
        }

        // Same refusal for work that was never decided: the rebase counts
        // are server truth and include the decisions just sent, so a
        // non-zero Remaining means at least one conflict has no decision.
        // Committing them re-arms the footer gate, which then shows
        // `Remaining conflicts: n` and disables Submit.
        if (applied.serverCounts.Remaining > 0) {
          commitRebase(applied);
          return;
        }

        try {
          // Step 3.
          const res = await ds.submit(ids, buildSubmitRequest(applied));
          if (!mountedRef.current) {
            return;
          }
          // Step 6.
          announce(res);
          return;
        } catch (err) {
          const apiError = toMergeApiError(err);
          // Step 4. One more pass through rebase → check → submit, and only
          // for a REBASE_REQUIRED that came back from submit. A rebase that
          // itself asks to be rebased is surfaced, not looped — the same
          // rule `useMergeSession` applies.
          if (
            apiError.code !== 'REBASE_REQUIRED' ||
            attempt === MAX_ATTEMPTS - 1
          ) {
            throw apiError;
          }
          // The next pass rebases again from what this one committed.
        }
      }
    } catch (err) {
      // Step 7. Surfaced, and nothing optimistic is written: no decision is
      // marked resolved, no submission is marked submitted, and the rebase
      // that failed to lead anywhere is not committed, which leaves the
      // session on the older base — safe, because a later rebase from an
      // older base still reports everything that moved.
      if (!mountedRef.current) {
        return;
      }
      const apiError = toMergeApiError(err);
      // Step 5. The route not existing is a footer reason only.
      if (apiError.code === 'SUBMIT_UNAVAILABLE') {
        return;
      }
      // A row-set mismatch is terminal, so it blocks the screen instead of
      // raising a snackbar.
      const structural = detectStructuralBlock(apiError, false);
      if (structural.blocked) {
        sessionRef.current.dispatch({
          type: 'structural/set',
          blocked: true,
          message: structural.message,
        });
        return;
      }
      enqueueSnackbar(
        apiError.code === 'RESOLUTION_UNAVAILABLE'
          ? resolutionUnavailableMessage(apiError)
          : (SUBMIT_ERROR_MESSAGES[apiError.code] ?? apiError.message),
        { variant: 'error' }
      );
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [announce, buildSubmitRequest, commitRebase, ds, enqueueSnackbar, ids]);

  return {
    submit,
    submitting,
    outcome,
    newConflicts,
    dismissNewConflicts,
  };
}

export default useSubmitFlow;
